import React, { createContext, useContext, useMemo } from "react"
import _ from "lodash"

import { useLayout } from "@Quiz/layout"
import { useElementSize } from "@helper/useElementSize"

const COUNTDOWN_BULLETS_MOBILE = 18
const COUNTDOWN_BULLETS_DESKTOP = 34

interface ICountdownContext {
  hasCountdown: boolean
  perc: number // 0..100
  started: boolean
}

export const CountdownContext = createContext<ICountdownContext>({
  hasCountdown: false,
  perc: 0,
  started: false
})
CountdownContext.displayName = "CountdownContext"

export const useCountdown = () => useContext(CountdownContext)

type ICountdownVariant = "muchTime" | "middle" | "almostOver" | "notStarted"

const COUNTDOWN_DESKTOP_WIDTH = 480 // keep in sync with CSS
const SHRINK_COUNTDOWN_WHEN_WIDTH_BELOW = 820

export const Countdown: React.FC = () => {
  const { hasCountdown, perc, started } = useCountdown()
  const { type: layoutType } = useLayout()
  const { ref: holderRef, size: holderSize } = useElementSize<HTMLDivElement>()

  const countdownAdditionalStyle = useMemo(() => {
    if (layoutType === "mobile") { return undefined }
    if (holderSize.width === 0) { return undefined }
    if (holderSize.width < SHRINK_COUNTDOWN_WHEN_WIDTH_BELOW) {
      const spaceNeededForOtherStuff = SHRINK_COUNTDOWN_WHEN_WIDTH_BELOW - COUNTDOWN_DESKTOP_WIDTH
      const spaceLeft = holderSize.width - spaceNeededForOtherStuff      
      return {transform: `scale(${spaceLeft / COUNTDOWN_DESKTOP_WIDTH})`}
    }
  }, [layoutType, holderSize])

  if (!hasCountdown) { return null }  

  const numBullets = layoutType === "desktop" ? COUNTDOWN_BULLETS_DESKTOP : COUNTDOWN_BULLETS_MOBILE
  let variant: ICountdownVariant = started ? "muchTime" : "notStarted"
  if (perc < 25) {
    variant = "almostOver"
  } else if (perc < 51) {
    variant = "middle"
  }

  return (
    <div className="neo__countdown__holderOuter" ref={holderRef}>
      <div className="neo__countdown__holder">
        <div className={`neo__countdown neo__countdown--${variant}`} style={countdownAdditionalStyle}>
          {_.times(numBullets, (i) => {
            const visible = perc > 0 && (((i * 1.0 / (numBullets - 1)) * 100) <= perc)
            return (
              <div key={i} className={`neo__countdown__bullet ${visible ? "" : "--hidden"}`} />
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default Countdown
